import { Injectable } from "@angular/core";
import { db, JobIssue, JobIssueVendor, SyncableStatus, UserVendorContact } from "../../../core/database/db";
import { v4 as uuidv4 } from "uuid";

import { NO_VENDOR_ASSIGNED_IDENTIFIER, SELF_ASSIGNED_VENDOR_ASSIGNMENT, VendorDTO } from "../../vendors/models/VendorDTO";
import { hasUsableVendorContact, UserVendorContactService } from "../../vendors/services/user-vendor-contact.service";
import { VendorDeliveryState } from "../../vendors/models/JobIssueVendorDTO";

/** Pairs a source issue with the newly cloned issue that should inherit its vendor assignments. */
export interface VendorAssignmentCloneLink {
  sourceIssueIdentifier: string;
  targetIssueIdentifier: string;
}

@Injectable({ providedIn: "root" })
export class JobIssueVendorService {

  constructor(private userVendorContactService: UserVendorContactService) { }

  public getFirstActiveVendorForIssue(issueIdentifier: string): Promise<JobIssueVendor | undefined> {
    return db.jobIssueVendor
      .where("issueIdentifier").equals(issueIdentifier)
      .and(v => !v.deleted)
      .first();
  }

  public getAllActiveVendorsForIssue(issueIdentifier: string): Promise<JobIssueVendor[]> {
    return db.jobIssueVendor
      .where("issueIdentifier").equals(issueIdentifier)
      .and(v => !v.deleted)
      .toArray();
  }

  public getAllActiveVendorsForIssues(issueIdentifiers: string[]): Promise<JobIssueVendor[]> {
    return db.jobIssueVendor
      .where("issueIdentifier").anyOf(issueIdentifiers)
      .and(v => !v.deleted)
      .toArray();
  }

  /**
   * Copies the given vendor assignments onto a newly created issue, preserving the trade partner and
   * its sent/viewed delivery state so a carried-over issue keeps the history the CM already sees.
   * No change when the target already has an assignment so repeated clones stay the same.
   */
  public async cloneVendorAssignmentsToIssue(
    sourceVendors: JobIssueVendor[],
    targetIssueIdentifier: string,
    updatedAt: Date
  ): Promise<JobIssueVendor[]> {
    if (sourceVendors.length === 0) {
      return [];
    }

    const existing = await this.getAllActiveVendorsForIssue(targetIssueIdentifier);
    if (existing.length > 0) {
      return [];
    }

    const clones = sourceVendors.map(vendor => this.buildAssignmentClone(vendor, targetIssueIdentifier, updatedAt));
    const newIds = await db.jobIssueVendor.bulkAdd(clones, { allKeys: true });

    return clones.map((clone, index) => ({ ...clone, id: newIds[index] }));
  }

  /**
   * Copies each source issue's active vendor assignments onto its freshly cloned child issue, so the
   * trade partner plus sent/viewed status carries forward when a BQ step's issues become open items.
   */
  public async cloneVendorAssignmentsForIssues(
    links: VendorAssignmentCloneLink[],
    updatedAt: Date
  ): Promise<void> {
    if (links.length === 0) {
      return;
    }

    const sourceIdentifiers = Array.from(new Set(links.map(link => link.sourceIssueIdentifier)));
    const targetIdentifiers = Array.from(new Set(links.map(link => link.targetIssueIdentifier)));

    const [sourceVendors, existingTargetVendors] = await Promise.all([
      this.getAllActiveVendorsForIssues(sourceIdentifiers),
      this.getAllActiveVendorsForIssues(targetIdentifiers)
    ]);

    if (sourceVendors.length === 0) {
      return;
    }

    const targetsWithAssignments = new Set(existingTargetVendors.map(vendor => vendor.issueIdentifier));
    const vendorsBySourceIdentifier = sourceVendors.reduce((map, vendor) => {
      map.set(vendor.issueIdentifier, [...(map.get(vendor.issueIdentifier) ?? []), vendor]);
      return map;
    }, new Map<string, JobIssueVendor[]>());

    const clones = links
      .filter(link => !targetsWithAssignments.has(link.targetIssueIdentifier))
      .flatMap(link => (vendorsBySourceIdentifier.get(link.sourceIssueIdentifier) ?? [])
        .map(vendor => this.buildAssignmentClone(vendor, link.targetIssueIdentifier, updatedAt)));

    if (clones.length === 0) {
      return;
    }

    await db.jobIssueVendor.bulkAdd(clones);
  }

  /** Builds an unsaved copy of a vendor assignment pointed at another issue. */
  private buildAssignmentClone(
    sourceVendor: JobIssueVendor,
    targetIssueIdentifier: string,
    updatedAt: Date
  ): JobIssueVendor {
    const { id: _id, ...vendorWithoutId } = sourceVendor;

    return {
      ...vendorWithoutId,
      issueVendorIdentifier: uuidv4(),
      issueIdentifier: targetIssueIdentifier,
      updatedAt,
      syncStatus: SyncableStatus.Pending
    };
  }

  public async persistVendorForIssue(
    issueIdentifier: string,
    externalVendorId: number | null,
    vendorName?: string
  ): Promise<JobIssueVendor> {
    const existingVendor = await this.getFirstActiveVendorForIssue(issueIdentifier);
    const updatedAt = new Date();

    const newVendor: JobIssueVendor = {
      issueIdentifier,
      issueVendorIdentifier: uuidv4(),
      externalVendorId: externalVendorId ?? 0,
      vendorName,
      updatedAt,
      createdAt: updatedAt,
      isSent: false,
      syncStatus: SyncableStatus.Pending
    };

    if (externalVendorId !== null) {
      if (existingVendor) {
        await db.jobIssueVendor.update(existingVendor.id!, {
          externalVendorId,
          vendorName,
          updatedAt,
          syncStatus: SyncableStatus.Pending
        });

        existingVendor.externalVendorId = externalVendorId;
        existingVendor.vendorName = vendorName;
        existingVendor.updatedAt = updatedAt;
        existingVendor.syncStatus = SyncableStatus.Pending;
      } else {
        const newVendorId = await db.jobIssueVendor.add(newVendor);
        newVendor.id = newVendorId;
      }
    } else if (existingVendor) {
      await db.jobIssueVendor.update(existingVendor.id!, {
        deleted: true,
        updatedAt,
        syncStatus: SyncableStatus.Pending
      });

      existingVendor.deleted = true;
      existingVendor.updatedAt = updatedAt;
      existingVendor.syncStatus = SyncableStatus.Pending;
    }

    return existingVendor ?? newVendor;
  }

  public async addOrReplaceVendorAssignmentForIssue(
    issueIdentifier: string,
    selectedVendor: VendorDTO | null,
    previousSelectedVendorAssignmentToReplace: JobIssueVendor | null
  ): Promise<JobIssueVendor> {
    if (previousSelectedVendorAssignmentToReplace && previousSelectedVendorAssignmentToReplace.issueIdentifier !== issueIdentifier) {
      throw new Error("Previous selected vendor assignment does not match the provided issue identifier.");
    }

    const allExistingVendorAssignmentsForIssue = await this.getAllActiveVendorsForIssue(issueIdentifier);
    const alreadySelectedVendorAssignmentForIssue = allExistingVendorAssignmentsForIssue.find(x => x.externalVendorId === selectedVendor?.id);

    const updatedAt = new Date();
    let vendorAssignmentResult: JobIssueVendor | null = null;

    const isANoopAddition = selectedVendor && alreadySelectedVendorAssignmentForIssue;
    const isANoopReplacement = selectedVendor && previousSelectedVendorAssignmentToReplace && selectedVendor.id === previousSelectedVendorAssignmentToReplace.externalVendorId;
    const isReplacingAPreviousAssignmentWithNewVendor = !!previousSelectedVendorAssignmentToReplace && !!selectedVendor && previousSelectedVendorAssignmentToReplace.externalVendorId !== selectedVendor.id;
    const isReplacingAPreviousAssignmentWithNoVendor = !!previousSelectedVendorAssignmentToReplace && !!selectedVendor === false;
    const isAddingNewVendorRegardlessWithoutAffectingPreviousAssignments = !!selectedVendor
      && isReplacingAPreviousAssignmentWithNewVendor === false
      && isReplacingAPreviousAssignmentWithNoVendor === false;

    if (isANoopAddition) {
      vendorAssignmentResult = alreadySelectedVendorAssignmentForIssue;
    } else if (isANoopReplacement) {
      vendorAssignmentResult = previousSelectedVendorAssignmentToReplace;
    } else if (isAddingNewVendorRegardlessWithoutAffectingPreviousAssignments) {
      const newVendorAssignment: JobIssueVendor = {
        issueIdentifier,
        issueVendorIdentifier: uuidv4(),
        externalVendorId: selectedVendor.id,
        vendorName: selectedVendor.doingBusinessAs || selectedVendor.name,
        updatedAt,
        createdAt: updatedAt,
        isSent: false,
        syncStatus: SyncableStatus.Pending
      };

      const newVendorId = await db.jobIssueVendor.add(newVendorAssignment);
      newVendorAssignment.id = newVendorId;
      vendorAssignmentResult = newVendorAssignment;

    } else if (isReplacingAPreviousAssignmentWithNewVendor) {

      const updatedVendorAssignment: JobIssueVendor = {
        issueIdentifier,
        issueVendorIdentifier: previousSelectedVendorAssignmentToReplace.issueVendorIdentifier,
        externalVendorId: selectedVendor.id,
        vendorName: selectedVendor.doingBusinessAs || selectedVendor.name,
        updatedAt,
        createdAt: updatedAt,
        isSent: false,
        syncStatus: SyncableStatus.Pending
      };

      await db.jobIssueVendor.update(previousSelectedVendorAssignmentToReplace.id!, { ...updatedVendorAssignment });

      updatedVendorAssignment.id = previousSelectedVendorAssignmentToReplace.id;
      vendorAssignmentResult = updatedVendorAssignment;

    } else if (isReplacingAPreviousAssignmentWithNoVendor) {
      await db.jobIssueVendor.update(previousSelectedVendorAssignmentToReplace.id!, {
        deleted: true,
        updatedAt,
        syncStatus: SyncableStatus.Pending
      });

      vendorAssignmentResult = {
        ...previousSelectedVendorAssignmentToReplace,
        deleted: true,
        updatedAt,
        syncStatus: SyncableStatus.Pending
      };
    }

    return vendorAssignmentResult!;
  }

}

export function determinVendorAssignmentStatesForIssue(issue: JobIssue, vendorAssignmentsForIssue: JobIssueVendor[], contactsForVendors: UserVendorContact[]): Record<string, VendorDeliveryState> {
  const vendorStates: Record<string, VendorDeliveryState> = {};

  if (issue.isSelfAssigned) {
    vendorStates[SELF_ASSIGNED_VENDOR_ASSIGNMENT.issueVendorIdentifier] = VendorDeliveryState.NotSent;
  }

  for (const assignment of vendorAssignmentsForIssue) {
    const contactsForThisVendor = contactsForVendors.filter(contact => contact.externalVendorId === assignment.externalVendorId);
    const hasUsableContact = hasUsableVendorContact(contactsForThisVendor);
    const {
      isSent: assigmentIsSent,
      viewedAt: assignmentViewedAt,
      createdAt: assigmentCreatedAt,
      lastSentAt: assignemntLastSentAt
    } = assignment;

    const assignmentHasBeenViewed = assigmentIsSent && assignmentViewedAt;
    const assignmentHasBeenViewedSinceIssueWasCreated = assignmentHasBeenViewed && assignmentViewedAt > assigmentCreatedAt;
    const assignmentHasBeenSent = assigmentIsSent && assignemntLastSentAt;

    if (hasUsableContact === false) {
      vendorStates[assignment.issueVendorIdentifier] = VendorDeliveryState.ContactRequired;
    } else if (assignmentHasBeenViewedSinceIssueWasCreated) {
      vendorStates[assignment.issueVendorIdentifier] = VendorDeliveryState.Viewed;
    } else if (assignmentHasBeenSent) {
      vendorStates[assignment.issueVendorIdentifier] = VendorDeliveryState.Sent;
    } else {
      vendorStates[assignment.issueVendorIdentifier] = VendorDeliveryState.NotSent;
    }
  }

  if (Object.keys(vendorStates).length === 0) {
    vendorStates[NO_VENDOR_ASSIGNED_IDENTIFIER] = VendorDeliveryState.NoVendorAssigned;
  }

  return vendorStates;
}